import { isBatesColor, stripUnencodable } from './pdfOverlay';

/**
 * The Bates label rule and its bounds (VE-24447) — one implementation, five
 * callers: the burn-in worker that draws it, the editor that previews it (the
 * modal and the on-canvas overlay), the export audit that reports its range,
 * the asset reader that validates it on load, and the audit trails that
 * describe a sequence in one line (`describeBatesStamp`, at the foot of this
 * file — the per-file one in `sagas/auditLogs.ts` and the merged document's in
 * `projects/auditEvents.ts` must say the same thing). Which number each page
 * carries (VE-30106: its original position) is decided here too.
 *
 * LEAF MODULE — imports only other leaves (`./pdfOverlay`, which itself imports
 * nothing). It has to be reachable from the burn-in
 * worker, which `test/burn-in/*.mjs` loads through node's type stripping with
 * no bundler and no DOM, AND from a React component. `models.ts` is where
 * `BatesStampSettings` lives and would otherwise be the natural home, but it
 * pulls in `watermarkImage` and `@helpers/externalIntegrationApi`, so the
 * worker cannot reach it. Hence a leaf of its own rather than a second copy of
 * the rule — the modal previously reimplemented the padding inline and the two
 * had already drifted (`Math.trunc` on one side, not the other).
 *
 * **The clamps live here, not at the UI.** The modal is a widget control; the
 * redaction-data asset in object storage is the trust boundary, and it is
 * hand-editable and read on every document open. Every bound below is enforced
 * by the loader (`batesStampFromAssetBody`) so that a hostile or corrupt record
 * degrades to something harmless instead of reaching the worker — where an
 * unbounded `digits` killed the entire export with `padStart`'s "Invalid string
 * length", and a `startNumber` past 2^53 gave every page the same number.
 */

/**
 * Upper bound on zero-padding width. 12 digits is far past any real production
 * (a trillion pages) and keeps `padStart` cheap; the modal offers the same
 * ceiling so the two agree.
 */
export const MAX_BATES_DIGITS = 12;

/**
 * Upper bound on the starting number. `Number.MAX_SAFE_INTEGER` is the real
 * constraint: past it, `startNumber + pageIndex` stops incrementing in float
 * arithmetic and every page is stamped with the SAME number — the one failure
 * an identifier must never have. Held well below it so a long document cannot
 * walk across the boundary mid-sequence.
 */
export const MAX_BATES_START_NUMBER = 1_000_000_000_000;

/** Point-size bounds. Below ~4pt is unreadable; above ~72pt cannot be a stamp. */
export const MIN_BATES_FONT_SIZE = 4;
export const MAX_BATES_FONT_SIZE = 72;

/** Max characters accepted in the prefix / suffix fields. */
export const MAX_BATES_AFFIX_LENGTH = 20;

/**
 * Distance from the page edge to the stamp, in PDF points.
 *
 * 0.5 inches. PM's answer on VE-24447 (2026-09-09): *"the default offset for
 * the bates stamp is typically .5 inches"* — the production convention, and the
 * decision of record.
 *
 * The approved prototype uses 10pt (`vpe-design/Redact Prototype`,
 * `BATES_MARGIN_PT`), which sits noticeably tighter to the edge. That value
 * PREDATES the answer above and is superseded by it. Noted because the design
 * and the running app visibly differ here, and the next person to compare them
 * should not "fix" the app back.
 *
 * ABSOLUTE, unlike a watermark's `inset`, which is a FRACTION of the smaller
 * page dimension. A fraction is right for decoration — it keeps the visual gap
 * proportionate — but wrong here: the same fraction is a different physical
 * margin on Letter than on A4, and a Bates margin is a measured convention, not
 * a look. Both the burn-in worker and the on-canvas preview convert it to the
 * fraction `anchoredCenterPt` wants against each page's own size, so it is the
 * same number of points everywhere.
 *
 * Lives here rather than in the worker because the preview needs the same
 * number, and two copies of it would drift. Every comment and assertion that
 * depends on the margin reads it from here, so changing this line is the whole
 * change.
 *
 * Now the DEFAULT rather than the only value — a stamp may carry its own
 * `insetXPt` / `insetYPt` — and the default an absent one resolves to, which is
 * why every caller goes through `batesInsetXPt` / `batesInsetYPt` instead of
 * reading this directly.
 */
export const BATES_INSET_PT = 36;

/** PDF points per inch. The margin control speaks inches; the model stores points. */
export const PT_PER_INCH = 72;

/**
 * Margin bounds, in points. Zero is a legitimate request (flush to the trim
 * edge) and the ceiling is two inches — past that the stamp is no longer a
 * margin mark, and on a small page the label's shrink-to-fit would be fighting
 * the margin for the same width.
 *
 * `anchoredCenterPt` applies its own fractional ceiling downstream, so a margin
 * this wide on a very small page still cannot push the label off the sheet.
 */
export const MIN_BATES_INSET_PT = 0;
export const MAX_BATES_INSET_PT = 2 * PT_PER_INCH;

export const clampBatesInsetPt = (v: unknown): number =>
  typeof v === 'number' && Number.isFinite(v)
    ? Math.min(MAX_BATES_INSET_PT, Math.max(MIN_BATES_INSET_PT, v))
    : BATES_INSET_PT;

/**
 * The margins a stamp actually draws at: its own values, or the 0.5in default
 * for whichever it does not carry.
 *
 * TWO of them, because the side margin and the header/footer margin are
 * separate production conventions — an operator can unlink them, and a stamp
 * that never was still resolves both to the same default. Equal values are
 * exactly what "linked" means; nothing stores the link itself.
 *
 * Structurally typed so this stays a LEAF — the worker reaches it through
 * node's type stripping and cannot import `models.ts` (see the module header).
 * Both the worker and the on-canvas preview call these, which is the point: a
 * stamp saved before the margins were configurable carries neither, and the two
 * sides must agree on what that falls back to.
 */
export const batesInsetXPt = (settings: {
  readonly insetXPt?: number;
}): number =>
  settings.insetXPt === undefined
    ? BATES_INSET_PT
    : clampBatesInsetPt(settings.insetXPt);

export const batesInsetYPt = (settings: {
  readonly insetYPt?: number;
}): number =>
  settings.insetYPt === undefined
    ? BATES_INSET_PT
    : clampBatesInsetPt(settings.insetYPt);

/**
 * The sequence number a page carries: its position in the ORIGINAL document.
 *
 * VE-30106 — the decision of record: deleted pages ("Delete Page") keep their
 * number. They are dropped from the output, so that number is never drawn, and
 * the sequence the export carries has a gap where they were. Deleting original
 * page 1 leaves original page 2 on `startNumber + 1`. Pages merely REMOVED
 * ("Remove Page") survive as blank slip sheets and are stamped as usual. The
 * VE-24447 rule — survivors renumbered contiguously — misread PM's "delete
 * original page 1 and stamping starts at 2", which describes exactly this.
 *
 * Nothing but the page's own position feeds it: not which other pages are
 * deleted, and not whether they were deleted before or after the stamp was
 * applied. The worker, the preview and the export audit all number through
 * this one function.
 */
export function batesNumberForPage(
  startNumber: number,
  pageNumber: number
): number {
  return startNumber + (pageNumber - 1);
}

const pageCount = (totalPages: number): number =>
  Number.isSafeInteger(totalPages) ? Math.max(0, totalPages) : 0;

/**
 * The pages actually deleted from the output: the entries of `excludedPages`
 * that are integer pages in `[1, totalPages]`, once each.
 *
 * `excludedPages` is untrusted — it is read from the redaction-data asset,
 * which is hand-editable — so this is the ONE reading of it. The burn-in worker
 * deletes exactly these pages (and skips redacting them), and
 * `survivingOriginalPages` is their complement, so the pages the worker
 * removes and the original-page map the Bates stamp numbers from cannot
 * disagree. (A raw fractional entry would reach `deletePage(1.5)`, which mupdf
 * truncates — deleting a page nothing else considers deleted.)
 */
export function validDeletedPages(
  totalPages: number,
  excludedPages: readonly number[]
): Set<number> {
  const total = pageCount(totalPages);
  return new Set(
    excludedPages.filter((p) => Number.isInteger(p) && p >= 1 && p <= total)
  );
}

/**
 * The original page numbers that reach the output, ascending — so index `i`
 * is output page `i`. The ONE definition of "which pages survive" the worker
 * stamps from and the export audit reports from: the complement of
 * `validDeletedPages`, so a bad `excludedPages` record can neither fail the
 * export nor shift a number. O(totalPages).
 */
export function survivingOriginalPages(
  totalPages: number,
  excludedPages: readonly number[]
): number[] {
  const total = pageCount(totalPages);
  const deleted = validDeletedPages(total, excludedPages);
  const surviving: number[] = [];
  for (let p = 1; p <= total; p++) {
    if (!deleted.has(p)) {
      surviving.push(p);
    }
  }
  return surviving;
}

/**
 * The first and last numbers an export actually stamps — what its audit entry
 * names as the production's range. Null when no page survives. Only deleting
 * the first or last page moves an end; a deleted page in between is a gap
 * inside the range, not a shorter one.
 */
export function batesRangeForExport(
  startNumber: number,
  totalPages: number,
  excludedPages: readonly number[]
): { first: number; last: number } | null {
  const surviving = survivingOriginalPages(totalPages, excludedPages);
  const firstPage = surviving[0];
  const lastPage = surviving[surviving.length - 1];
  if (firstPage === undefined || lastPage === undefined) {
    return null;
  }
  return {
    first: batesNumberForPage(startNumber, firstPage),
    last: batesNumberForPage(startNumber, lastPage),
  };
}

const clampInt = (
  n: unknown,
  lo: number,
  hi: number,
  fallback: number
): number =>
  typeof n === 'number' && Number.isFinite(n)
    ? Math.min(hi, Math.max(lo, Math.trunc(n)))
    : fallback;

export const clampBatesDigits = (n: unknown): number =>
  clampInt(n, 0, MAX_BATES_DIGITS, 0);

export const clampBatesStartNumber = (n: unknown): number =>
  clampInt(n, 0, MAX_BATES_START_NUMBER, 1);

export const clampBatesFontSize = (n: unknown): number =>
  typeof n === 'number' && Number.isFinite(n) && n > 0
    ? Math.min(MAX_BATES_FONT_SIZE, Math.max(MIN_BATES_FONT_SIZE, n))
    : MIN_BATES_FONT_SIZE;

/**
 * Trim an affix to its length bound. Non-strings become undefined.
 *
 * Slices by CODE POINT, not UTF-16 unit: `String.slice` at a fixed index can cut
 * a surrogate pair in half, and the lone surrogate that leaves behind is then
 * written into session state and `JSON.stringify`'d into object storage. The
 * downstream encoder drops it, so nothing renders wrong — but a malformed string
 * in a persisted record is a defect on its own.
 */
export const clampBatesAffix = (s: unknown): string | undefined => {
  if (typeof s !== 'string' || s.length === 0) {
    return undefined;
  }
  // LENGTH ONLY. Encodability is a RENDERING limit and belongs on the way to the
  // renderer — see `drawableBatesSettings`. Folding it in here made the asset
  // reader lossy, and because the session is written straight back by autosave,
  // one open-and-edit cycle permanently destroyed the operator's original prefix
  // in object storage. A bound on length is safe to persist; a bound imposed by
  // today's font is not.
  const kept = [...s].slice(0, MAX_BATES_AFFIX_LENGTH).join('');
  return kept.length > 0 ? kept : undefined;
};

/**
 * A start number the per-page increment can survive. The modal validates with
 * this rather than `Number.isInteger`, which is `true` for `1e308`.
 */
export const isUsableBatesStartNumber = (n: number): boolean =>
  Number.isSafeInteger(n) && n >= 0 && n <= MAX_BATES_START_NUMBER;

/**
 * The label for one page: `prefix` + zero-padded number + `suffix`.
 *
 * `digits` is a MINIMUM width, not a maximum — a sequence that outgrows its
 * declared padding keeps counting (…0099, 0100) rather than truncating or
 * wrapping, because losing a digit would make two pages share a number, which
 * for an identifier is worse than an inconsistent width.
 */
export function formatBatesLabel(
  settings: {
    readonly digits: number;
    readonly prefix?: string;
    readonly suffix?: string;
  },
  n: number
): string {
  const digits = clampBatesDigits(settings.digits);
  const num = Number.isFinite(n) ? Math.trunc(n) : 0;
  return `${settings.prefix ?? ''}${String(num).padStart(digits, '0')}${
    settings.suffix ?? ''
  }`;
}

/**
 * Put every bounded field of a settings record inside its bound.
 *
 * The clamp every layer applies: the asset reader (`batesStampFromAssetBody`),
 * the export mapper (`toBurnInBatesStamp`) and the burn-in worker itself.
 *
 * PERSISTENCE-SAFE, deliberately: every bound here is a property of the DATA
 * (a number out of range, an affix longer than the field allows, a colour that
 * is not a colour), so writing the clamped value back is a repair. What this
 * does NOT do is drop characters the current font cannot draw — that is a
 * property of the RENDERER, it belongs in `drawableBatesSettings`, and
 * persisting it would destroy the operator's text. Round 2 of review found the clamps applied at only one
 * of those, which left two defects live one layer in — `startNumber` past 2^53
 * still gave every page the SAME number, and a 200KB prefix still drew the
 * stamp off the page, for anything that reached the worker without passing the
 * reader. `casemedia` already has a second asset-writing path, so "nothing else
 * reaches it today" is a statement with a short shelf life.
 *
 * Clamping, not rejecting. A visibly-wrong stamp an operator can see and
 * correct beats a silently-absent sequence on a document that had one — which
 * is the failure the same review found when one layer chose to reject instead.
 *
 * Anchors are repaired rather than clamped: `anchorV` is narrower than
 * `WatermarkAnchorV` (U3-FR3 says header or footer, so `'middle'` is not a
 * legal Bates value) and an out-of-range anchor falls back to footer-right.
 */
// `isBatesColor` and `stripUnencodable` both live in `pdfOverlay`, which owns
// the PDF text/colour grammars. Importing them keeps ONE expression of each —
// a second copy here is how a preview ends up accepting what an export rejects.
export function clampBatesSettings<
  T extends {
    digits: number;
    startNumber: number;
    prefix?: string;
    suffix?: string;
    anchorH: 'left' | 'center' | 'right';
    anchorV: 'top' | 'bottom';
    fontSize: number;
    color?: string;
    bold?: boolean;
    insetXPt?: number;
    insetYPt?: number;
  },
>(b: T): T {
  return {
    ...b,
    digits: clampBatesDigits(b.digits),
    startNumber: clampBatesStartNumber(b.startNumber),
    prefix: clampBatesAffix(b.prefix),
    suffix: clampBatesAffix(b.suffix),
    anchorH:
      b.anchorH === 'left' || b.anchorH === 'center' || b.anchorH === 'right'
        ? b.anchorH
        : 'right',
    anchorV: b.anchorV === 'top' ? 'top' : 'bottom',
    fontSize: clampBatesFontSize(b.fontSize),
    color: isBatesColor(b.color) ? b.color : undefined,
    bold: b.bold === true,
    // Absent stays absent — it is how every stamp written before the margins
    // were configurable reads, and `batesInsetXPt`/`batesInsetYPt` resolve it
    // to the default. Writing the default in instead would rewrite those
    // records on open for no change in what they draw.
    insetXPt:
      b.insetXPt === undefined ? undefined : clampBatesInsetPt(b.insetXPt),
    insetYPt:
      b.insetYPt === undefined ? undefined : clampBatesInsetPt(b.insetYPt),
  };
}

/**
 * `clampBatesSettings`, plus the affixes reduced to what the burn-in font can
 * actually DRAW. For the two layers about to draw — the export mapper and the
 * worker — and for no one else.
 *
 * ("drawable", not "renderable", only because eslint's testing-library rule
 * treats any `render*` identifier as a React render helper.)
 *
 * Kept separate from `clampBatesSettings` because the two do different kinds of
 * work and only one of them is safe to write back. Encodability is a limit of
 * today's WinAnsi simple font, not a fact about the document: if the worker ever
 * embeds a real font, a stored "機密-" starts rendering, whereas a stored "-"
 * has already lost it forever. The asset reader therefore keeps what it was
 * given, the modal refuses new unencodable input with a message, and the
 * stripping happens here, at the last moment, where it cannot be persisted.
 *
 * Also keeps `widthEm` honest: the worker measures and draws the SAME string.
 * When the strip happened later than the measurement, a right-anchored stamp
 * with dropped characters sat 30pt off its inset.
 */
export function drawableBatesSettings<
  T extends Parameters<typeof clampBatesSettings>[0],
>(b: T): T {
  const clamped = clampBatesSettings(b);
  return {
    ...clamped,
    prefix: stripUnencodable(clamped.prefix),
    suffix: stripUnencodable(clamped.suffix),
  };
}

const inchesLabel = (pt: number): string =>
  `${Math.round((pt / PT_PER_INCH) * 100) / 100}in`;

/**
 * What a Bates sequence IS, in one line — the audit trail's description of it.
 *
 * Leads with the label the first page will actually carry, built by the same
 * `formatBatesLabel` the worker draws with — the affixes, the padding and the
 * start number are all legible from it, and it is the string someone reading
 * this log will have in front of them on the released PDF. The rest is what
 * that string does not say: where it sits, and how it is drawn.
 *
 * Shade, weight and margins appear only when they differ from the defaults, so
 * a routine stamp logs a short line and an unusual one logs what made it
 * unusual.
 *
 * HERE RATHER THAN IN `sagas/auditLogs.ts`, where it was written: the merged
 * document's own stamping events moved out of the document editor's saga tree
 * and into `projects/auditEvents.ts` (VE-31977), and both sides must describe
 * a sequence the same way or one release's log reads differently from the
 * next. This module is already the leaf every Bates caller shares.
 */
export function describeBatesStamp(s: {
  readonly digits: number;
  readonly prefix?: string;
  readonly suffix?: string;
  readonly startNumber: number;
  readonly anchorV: string;
  readonly anchorH: string;
  readonly fontSize: number;
  readonly bold?: boolean;
  readonly color?: string;
  readonly insetXPt?: number;
  readonly insetYPt?: number;
}): string {
  const parts = [
    formatBatesLabel(s, s.startNumber),
    `start ${s.startNumber}`,
    `${s.digits} digits`,
    `${s.anchorV === 'top' ? 'header' : 'footer'} ${s.anchorH}`,
    `${s.fontSize}pt`,
  ];
  // Bold is the default, so it is the regular weight that is worth naming.
  if (!s.bold) {
    parts.push('regular weight');
  }
  if (s.color) {
    parts.push(`shade ${s.color}`);
  }
  const x = batesInsetXPt(s);
  const y = batesInsetYPt(s);
  if (x !== BATES_INSET_PT || y !== BATES_INSET_PT) {
    parts.push(
      x === y
        ? `margins ${inchesLabel(x)}`
        : `margins ${inchesLabel(x)} sides / ${inchesLabel(y)} top-bottom`
    );
  }
  return parts.join(' · ');
}
